import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { IForm } from "@/interfaces/forms";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { IFieldsForm } from "@/interfaces/fieldsForm";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  createFieldForm,
  createForm,
  deleteForm,
  deleteFieldForm,
  listFieldsForm,
  listForms,
  showForm,
  updateFieldForm,
  updateForm,
} from "@/services";

const DEFAULT_DATA: IForm = {
  id: 0,
  name: "",
  description: "",
  active: true,
  required: false,
  createdBy: undefined,
  formCategories: [],
  checklist: [],
  conditionals: []
};

export const useForms = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { idForm: id } = useParams();
  const [forms, setForms] = useState<IForm[]>([]);
  const [form, setForm] = useState<IForm>(DEFAULT_DATA);
  const { filter, setFilter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalForms, setTotalForms] = useState<number>(0);
  const [tempFieldsForms, setTempFieldsForms] = useState<IFieldsForm[]>(
    () => [],
  );
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: "/towermanagement/forms",
  });
  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleForm = (key: string, value: any, custom?: (v: any) => any) => {
    setForm((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const addFieldToForm = useCallback(
    (fieldId: number, fieldData: any) => {
      if (!fieldId || !fieldData) return;
      setTempFieldsForms((prevFields) => {
        const currentFields = Array.isArray(prevFields) ? prevFields : [];
        let nextOrder = 1;
        if (currentFields.length > 0) {
          const orderValues = currentFields.map((field) => field.order || 0);
          nextOrder = orderValues.length > 0 ? Math.max(...orderValues) + 1 : 1;
        }
        const existingField = currentFields.find(
          (field) => field.idField === fieldId,
        );
        if (existingField) return currentFields;

        const newField: IFieldsForm = {
          id: Date.now(),
          idField: fieldId,
          idForm: form.id || 0,
          required: fieldData.required || false,
          order: nextOrder,
          field: fieldData,
          multiple: fieldData.multiple || false,
          min: fieldData.min || 0,
          max: fieldData.max || 0,
          priority: fieldData.priority || "medium",
          idFormGroup: 0,
        };

        const newFields = [...currentFields, newField];
        const sortedFields = [...newFields].sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        return sortedFields;
      });
    },
    [form.id],
  );

  const updateTempField = (
    fieldId: number | string,
    data: Partial<IFieldsForm>,
  ) => {
    setTempFieldsForms((prevFields) => {
      if (!Array.isArray(prevFields)) return [];
      const updatedFields = prevFields.map((field) => {
        if (field.id === fieldId) {
          const updatedField = { ...field, ...data };
          if (data.field === undefined && field.field) {
            (updatedField as any).field = field.field;
          }
          return updatedField;
        }
        return field;
      });
      return [...updatedFields].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
  };

  const reorderTempFields = (newOrder: IFieldsForm[]) => {
    const updatedFields = newOrder.map((field, index) => ({
      ...field,
      order: index + 1,
    }));
    setTempFieldsForms(updatedFields);
  };

  const removeTempField = useCallback((fieldId: number | string) => {
    if (!fieldId) return;
    setTempFieldsForms((prevFields) => {
      const currentFields = Array.isArray(prevFields) ? prevFields : [];
      if (!currentFields.length) return currentFields;
      const filteredFields = currentFields.filter(
        (field) => field.id !== fieldId,
      );
      const reorderedFields = filteredFields.map((field, index) => ({
        ...field,
        order: index + 1,
      }));
      return reorderedFields;
    });
  }, []);

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showForm({ id, includes: [...includes, "formCategories"] })
      .then(async ({ form: FormsShow }) => {
        setForm(FormsShow);
        try {
          const { fieldsForms } = await listFieldsForm({
            filter: { idForm: id },
            limit: 1000,
            includes: ["field", "field.fieldOptions"],
          });
          const tempFields = fieldsForms.map((fieldsForm) => ({
            id: fieldsForm.id,
            idField: fieldsForm.idField,
            idForm: fieldsForm.idForm,
            required: fieldsForm.required,
            order: fieldsForm.order,
            field: fieldsForm.field,
            multiple: fieldsForm.multiple,
            min: fieldsForm.min,
            max: fieldsForm.max,
            priority: fieldsForm.priority,
          }));
          const sortedFields = tempFields.sort(
            (a, b) => (a.order || 0) - (b.order || 0),
          );
          setTempFieldsForms(sortedFields as IFieldsForm[]);
        } catch (error) {
          console.error("Erro ao carregar campos do formulário:", error);
        }
      })
      .finally(() => setLoading(false));
  };

  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter,
        sort,
      }: {
        page?: number;
        limit?: number;
        filter?: any;
        sort?: Record<string, any>;
      }) => {
        setLoading(true);
        listForms({
          includes: [...includes, "formAssignmentConditions"],
          filter,
          page,
          limit,
          sort,
        })
          .then(({ forms: listForms, limit, currentPage, totalItems }) => {
            setForms(listForms);
            
            setLimit(Number(limit));
            setTotalForms(totalItems);
          })
          .finally(() => setLoading(false));
      },
      300,
    ),
    [],
  );

  const reloadList = useCallback(async () => {
    const { forms: list, limit: resLimit, currentPage, totalItems } =
      await listForms({
        includes: [...includes, "formAssignmentConditions"],
        filter,
        page,
        limit,
        sort,
      });

    setForms(list);

    setLimit(resLimit);
    setTotalForms(totalItems);
  }, [filter, includes, limit, page, sort]);

  const search = useEntitySearch({ list: listForms, key: "forms", includes, filter });


  const create = async () => {
    try {
      setLoading(true);
      const { form: FormsCreate } = await createForm({ form });
      if (tempFieldsForms.length > 0) {
        for (const field of tempFieldsForms) {
          await createFieldForm({
            fieldsForm: {
              idForm: FormsCreate.id,
              idField: field.idField,
              required: field.required,
              order: field.order,
              multiple: field.multiple,
              min: field.min,
              max: field.max,
              priority: field.priority,
              idFormGroup: undefined
            },
          });
        }
      }
      setForm(FormsCreate);
      goEdit(FormsCreate.id);
      try {
        const { fieldsForms } = await listFieldsForm({
          filter: { idForm: FormsCreate.id },
          limit: 1000,
          includes: ["field", "field.fieldOptions"],
        });
        const tempFields = fieldsForms.map((fieldsForm) => ({
          id: fieldsForm.id,
          idField: fieldsForm.idField,
          idForm: fieldsForm.idForm,
          required: fieldsForm.required,
          order: fieldsForm.order,
          field: fieldsForm.field,
          multiple: fieldsForm.multiple,
          min: fieldsForm.min,
          max: fieldsForm.max,
          priority: fieldsForm.priority,
        }));
        const sortedFields = tempFields.sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        setTempFieldsForms(sortedFields as IFieldsForm[]);
      } catch (error) {
        console.error("Erro ao carregar campos após criação:", error);
      }
      toast.success(`Formulário criado com sucesso!`);
    } catch (error) {
      console.error("Erro ao criar formulário:", error);
      toast.error("Erro ao criar formulário");
    } finally {
      setLoading(false);
    }
  };

  const update = async () => {
    try {
      setLoading(true);
      const { form: formUpdate } = await updateForm({ id: form.id!, form });
      const { fieldsForms: currentFieldsForms } = await listFieldsForm({
        filter: { idForm: form.id },
        limit: 1000,
      });

      const tempIds = tempFieldsForms.map((f) => f.idField);
      const currentIds = currentFieldsForms.map((f) => f.idField);

      for (const field of currentFieldsForms) {
        if (!tempIds.includes(field.idField) && field.id) {
          await deleteFieldForm({ id: field.id });
        }
      }

      for (const tempField of tempFieldsForms) {
        const found = currentFieldsForms.find(
          (f) => f.idField === tempField.idField,
        );
        if (found && found.id) {
          if (
            found.required !== tempField.required ||
            found.order !== tempField.order
          ) {
            await updateFieldForm({
              id: found.id,
              fieldsForm: {
                ...found,
                required: tempField.required,
                order: tempField.order,
              },
            });
          }
        } else {
          await createFieldForm({
            fieldsForm: {
              idForm: form.id!,
              idField: tempField.idField,
              required: tempField.required,
              order: tempField.order,
              multiple: tempField.multiple,
              min: tempField.min,
              max: tempField.max,
              priority: tempField.priority,
              idFormGroup: undefined
            },
          });
        }
      }

      setForm(formUpdate);

      try {
        const { fieldsForms } = await listFieldsForm({
          filter: { idForm: form.id },
          limit: 1000,
          includes: ["field", "field.fieldOptions"],
        });
        const tempFields = fieldsForms.map((fieldsForm) => ({
          id: fieldsForm.id,
          idField: fieldsForm.idField,
          idForm: fieldsForm.idForm,
          required: fieldsForm.required,
          order: fieldsForm.order,
          field: fieldsForm.field,
          multiple: fieldsForm.multiple,
          min: fieldsForm.min,
          max: fieldsForm.max,
          priority: fieldsForm.priority,
        }));
        const sortedFields = tempFields.sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        setTempFieldsForms(sortedFields as IFieldsForm[]);
      } catch (error) {
        console.error("Erro ao recarregar campos após atualização:", error);
      }
    } catch (error) {
      console.error("Erro ao atualizar formulário:", error);
      toast.error("Erro ao atualizar formulário");
    } finally {
      setLoading(false);
    }
  };

  const deleteMany = async (ids: number[]) => {
    if (!ids?.length) return;

    setLoading(true);
    try {
      await Promise.all(ids.map((formId) => deleteForm({ id: formId })));
      toast.success(
        ids.length > 1
          ? `${ids.length} formulários excluídos com sucesso!`
          : `Formulário excluído com sucesso!`,
      );

      // Atualiza lista
      await reloadList();

      // Se o usuário estiver na tela do formulário excluído (deletar 1), volta
      if (ids.length === 1 && id && String(id) === String(ids[0])) {
        goBack();
      }
    } catch (error) {
      console.error("Erro ao excluir formulário:", error);
      toast.error("Erro ao excluir formulário");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  return {
    forms,
    form,
    handleForm,
    setForms,
    setForm,
    totalForms,
    filter,
    page,
    limit,
    loading,
    setFilter,
    setPage,
    setLimit,
    sort,
    handleFilter,
    handleSort,
    goEdit,
    goAdd,
    goBack,
    search,
    create,
    update,
    deleteMany,
    id,
    // ⬇️ IMPORTANTE: agora expomos o get
    get,
    // campos temporários
    tempFieldsForms,
    setTempFieldsForms,
    addFieldToForm,
    updateTempField,
    reorderTempFields,
    removeTempField,
  };
};
